// @flow

// DebugController manages all state for the experiment debugger and provides
// that state (and mutators) to children via context.

import React, { type Node, useState, useCallback } from 'react';
import throttle from 'lodash/throttle';
import DebugContext, { HIGHLIGHT_EXPERIMENTS_NONE } from 'app/packages/debugger/DebugContext';

type Props = {|
  children?: Node,
  canEnableDebugger: boolean,
  experiments: {
    triggerable: { [expName: string]: string },
    active: { [expName: string]: string },
  },
|};

export default function DebugController({ children, experiments, canEnableDebugger }: Props) {
  const [debuggerVisible, setDebuggerVisible] = useState(false);
  const [debuggingEnabled, setDebuggingEnabled] = useState(false);
  const [highlightedExperiment, highlightExperiment] = useState(HIGHLIGHT_EXPERIMENTS_NONE);
  const [overriddenExperiments, setOverriddenExperiments] = useState({});
  const [visibleExperimentCounts, setVisibleExperimentCounts] = useState({});

  // Specify the group that should be used for the given experiment name.
  // Passing null will reset the default group prior to overriding. Only active
  // when DebugConnector is enabled.
  const overrideExperiment = useCallback(
    (name: string, group: ?string) => {
      const newOverriddenExperiments = { ...overriddenExperiments };

      if (typeof group === 'string') {
        newOverriddenExperiments[name] = group;
      } else {
        delete newOverriddenExperiments[name];
      }

      setOverriddenExperiments(newOverriddenExperiments);
    },
    [overriddenExperiments, setOverriddenExperiments],
  );

  const highlightExperimentThrottled = useCallback(throttle(highlightExperiment, 1000), [
    highlightExperiment,
  ]);

  // DebugController ref-counts visible experiments and prunes any experiments
  // from the list with no instances.
  const setExperimentVisible = useCallback(
    (name: string, visible: boolean) => {
      setVisibleExperimentCounts((counts) => {
        // Increment.
        if (visible) {
          return { ...counts, [name]: (counts[name] || 0) + 1 };
        }

        // Decrement or clear if 0.
        if (counts[name] > 1) {
          return { ...counts, [name]: counts[name] - 1 };
        } else {
          const newCounts = { ...counts };
          delete counts[name];
          return newCounts;
        }
      });
    },
    [visibleExperimentCounts, setVisibleExperimentCounts],
  );

  return (
    <DebugContext.Provider
      value={{
        canEnableDebugger,
        setDebuggerVisible,
        debuggerVisible,
        setDebuggingEnabled,
        debuggingEnabled,
        highlightExperiment: highlightExperimentThrottled,
        highlightedExperiment,
        overrideExperiment,
        overriddenExperiments,
        visibleExperimentCounts,
        setExperimentVisible,
      }}
    >
      {children}
    </DebugContext.Provider>
  );
}
